import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { useInsightsPageParams } from '@/hooks';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { PointOfInterest, useSelectedPointOfInterestQuery } from '@/__generated__/graphql';
import { PoiReviewHeader } from './PoiReviewHeader';
import { ReviewHeaderShell } from './ReviewHeaderShell';
import { ImagePreviewContent } from './PointsOfInterest/SummaryCard/Preview/ImagePreviewDialog/ImagePreviewContent';

type ImagePreviewDrawerProps = {
  handleCloseAll: () => void;
  /** Called before clearing POI state/URL so ReviewScreen does not re-hydrate from a stale `poi` query param. */
  onBeginCloseFromPreview?: () => void;
};

export const ImagePreviewDrawer = ({
  handleCloseAll,
  onBeginCloseFromPreview,
}: ImagePreviewDrawerProps) => {
  const selectedPoiId = useAtomValue(poiState.selectedPointOfInterestId);
  const currentPoi = useAtomValue(poiState.selectedPointOfInterest) as PointOfInterest | undefined;
  const setSelectedPoiId = useSetAtom(poiState.selectedPointOfInterestId);
  const setSelectedPoi = useSetAtom(poiState.selectedPointOfInterest);
  const setEditPointOfInterest = useSetAtom(poiState.editPointOfInterest);
  const { clearPoi } = useInsightsPageParams();
  const navbarHeight = useNavbarHeight();

  const resetEditState = useCallback(() => {
    setEditPointOfInterest({
      state: 'Inactive',
      pointOfInterest: undefined,
      formState: undefined,
    });
  }, [setEditPointOfInterest]);

  useSelectedPointOfInterestQuery({
    variables: { pointOfInterestId: selectedPoiId || '' },
    skip: !selectedPoiId,
    onCompleted: (response) => {
      if (response?.pointOfInterest) setSelectedPoi(response.pointOfInterest);
    },
  });

  const handleBack = useCallback(() => {
    onBeginCloseFromPreview?.();
    resetEditState();
    setSelectedPoi(undefined);
    setSelectedPoiId(undefined);
    clearPoi();
  }, [onBeginCloseFromPreview, resetEditState, setSelectedPoi, setSelectedPoiId, clearPoi]);

  const handleCloseAllWithReset = useCallback(() => {
    resetEditState();
    handleCloseAll();
  }, [resetEditState, handleCloseAll]);

  return (
    <Drawer
      anchor="right"
      open={!!selectedPoiId}
      onClose={handleBack}
      // `disableEnforceFocus` lets inputs in PoiEditingForms (mounted outside this
      // Drawer's Modal) receive focus when edit actions are triggered from the preview.
      ModalProps={{ disableEnforceFocus: true }}
      slotProps={{
        root: {
          sx: (theme) => ({
            // Above the review drawer (drawer) but below the main AppBar (drawer + 50)
            zIndex: theme.zIndex.drawer + 2,
            [`& .${drawerClasses.paper}`]: {
              zIndex: theme.zIndex.drawer + 2,
            },
          }),
        },
      }}
      PaperProps={{
        sx: { width: '70vw', backgroundColor: '#fff' },
      }}
      transitionDuration={300}
    >
      <Stack
        sx={{
          paddingTop: `${navbarHeight}px`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {selectedPoiId && (
          <>
            {currentPoi ? (
              <>
                <PoiReviewHeader
                  currentPoi={currentPoi}
                  onClose={handleCloseAllWithReset}
                  onBack={handleBack}
                />
                <ImagePreviewContent />
              </>
            ) : (
              <>
                <ReviewHeaderShell
                  leftContent={null}
                  onClose={handleCloseAllWithReset}
                  onBack={handleBack}
                  backAriaLabel="Back to POI list"
                  closeAriaLabel="Close review panel"
                />
                <Stack flex={1} alignItems="center" justifyContent="center">
                  <CircularProgress />
                </Stack>
              </>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
};
